import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import auditLogService from '../services/AuditLogService';

export default function AuditLogs() {
    const { t } = useTranslation();
    const [from, setFrom] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
    const [to, setTo] = useState(dayjs().format('YYYY-MM-DD'));
    const [category, setCategory] = useState('');
    const [query, setQuery] = useState(null);
    const [cursor, setCursor] = useState(null);
    const [history, setHistory] = useState([]);
    const [data, setData] = useState({ items: [], nextCursor: null });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [validation, setValidation] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const types = ['authentication', 'access', 'administrator_activity'];
    const start = dayjs(from).startOf('day').unix();
    const end = dayjs(to).endOf('day').unix();
    const valid = Boolean(from && to && Number.isFinite(start) && Number.isFinite(end) && end >= start && end - start <= 90 * 86400);

    useEffect(() => {
        setQuery({ startDate: dayjs().subtract(7, 'day').startOf('day').unix(), endDate: dayjs().endOf('day').unix(), logType: '' });
    }, []);

    useEffect(() => {
        if (!query) return undefined;
        const controller = new AbortController();
        setLoading(true);
        setError('');
        setData({ items: [], nextCursor: null });
        auditLogService.list({ ...query, cursor, limit: 50 }, controller.signal)
            .then(result => { if (!controller.signal.aborted) setData(result); })
            .catch(err => { if (!controller.signal.aborted) setError(err.message); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [query, cursor, refresh]);

    const apply = () => {
        setValidation(!valid);
        if (!valid) return;
        setCursor(null);
        setHistory([]);
        setQuery({ startDate: start, endDate: end, logType: category });
    };
    const columns = ['time', 'category', 'actor', 'action', 'outcome', 'status', 'ip'];
    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h5" sx={{ mb: 1 }}>{t('auditLogs.title')}</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>{t('auditLogs.description')}</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <TextField type="date" label={t('auditLogs.from')} value={from} onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                    <TextField type="date" label={t('auditLogs.to')} value={to} onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                    <TextField select label={t('auditLogs.category')} value={category} onChange={e => setCategory(e.target.value)} size="small" sx={{ minWidth: 220 }}>
                        <MenuItem value="">{t('auditLogs.all')}</MenuItem>
                        {types.map(type => <MenuItem key={type} value={type}>{t(`auditLogs.${type}`)}</MenuItem>)}
                    </TextField>
                    <Button variant="contained" onClick={apply} disabled={loading}>{t('auditLogs.apply')}</Button>
                    <Button onClick={() => setRefresh(value => value + 1)} disabled={loading}>{t('auditLogs.refresh')}</Button>
                </Box>
                {validation && <Alert severity="warning" sx={{ mt: 2 }}>{t('auditLogs.invalidRange')}</Alert>}
            </Paper>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{t('auditLogs.loadError')}</Alert>}
            <TableContainer component={Paper} variant="outlined" aria-busy={loading}>
                <Table size="small" sx={{ minWidth: 1000 }}>
                    <TableHead><TableRow>{columns.map(column => <TableCell key={column}>{t(`auditLogs.${column}`)}</TableCell>)}</TableRow></TableHead>
                    <TableBody>
                        {data.items.map(row => <TableRow key={`${row.logType}:${row.eventKey}`} hover>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(row.createdAt * 1000).toLocaleString()}</TableCell>
                            <TableCell>{t(`auditLogs.${row.logType}`)}</TableCell>
                            <TableCell>{row.actorId || '—'}</TableCell>
                            <TableCell sx={{ overflowWrap: 'anywhere', maxWidth: 300 }}>{row.action}<Typography variant="caption" display="block" color="text.secondary">{row.method} {row.path}</Typography></TableCell>
                            <TableCell><Chip size="small" label={t(`auditLogs.${row.outcome}`)} color={row.outcome === 'success' ? 'success' : row.outcome === 'denied' ? 'warning' : 'error'} variant="outlined" /></TableCell>
                            <TableCell>{row.statusCode ?? '—'}</TableCell>
                            <TableCell>{row.sourceIp || '—'}</TableCell>
                        </TableRow>)}
                        {data.items.length === 0 && <TableRow><TableCell colSpan={columns.length} align="center" sx={{ py: 5 }}>
                            {loading ? <CircularProgress size={24} aria-label={t('auditLogs.loading')} /> : error ? t('auditLogs.loadError') : t('auditLogs.empty')}
                        </TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                <Typography variant="body2">{t('auditLogs.page', { number: history.length + 1 })}</Typography>
                <Button disabled={loading || !history.length} onClick={() => { setCursor(history[history.length - 1]); setHistory(history.slice(0, -1)); }}>{t('auditLogs.previous')}</Button>
                <Button disabled={loading || !data.nextCursor} onClick={() => { setHistory([...history, cursor]); setCursor(data.nextCursor); }}>{t('auditLogs.next')}</Button>
            </Box>
        </Box>
    );
}
